import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { Box, Typography, Button, Select, MenuItem, TextField, Skeleton, Alert } from '@mui/material';
import Grid from "@mui/material/Grid2";
import {
  GetVisitDetailsQuery,
  CommandIndexProvidersQuery,
  ShiftStatusEnum,
  useGetPatientInstructionsQuery,
  useSchedulerOrganizationalUnitsQuery,
  useOverrideVisitUnitProvidersQuery,
  useGenerateVisitsWarningsMutation,
  GenerateVisitsWarningsMutation
} from '~/graphql/__generated__/graphql';
import { compact, max } from 'lodash';
import { format, differenceInMinutes, isValid, closestTo } from 'date-fns';
import { TimeField } from '@mui/x-date-pickers';
import FilterGridRow from './FilterGridRow';
import { calculateDuration, getSchedulingOrganizationalUnitId, getServiceDefaults } from '~/utils/visitsHelper';
import VisitWarningsModal from './VisitWarningsModal';
import { useSnackbar } from '~/hooks/useSnackbar';
import { useOrgConfig, VisitResourceRequirement } from '~/providers/OrgConfigProvider';
import SelectServices from './SelectServices';
import ProviderSelect from './ProviderSelect';
import { defaultProviderData, getProviderShiftsInSameDay, isRequirementEligibleForShift, SelectProviderData } from '~/utils/visitValidationUtils';
import { useForm, Controller } from 'react-hook-form';
import ServiceInstructionsInput from './ServiceInstructionsInput';
import VisitNoteInput from "./VisitNoteInput";
import { timezoneAbbreviation } from '@caredna-marlin/utils/dates';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useZonedDateFormat } from '~/providers/ZonedDateFormatProvider';
import DatePicker from "~/components/common/forms/DatePicker";
import { getAutoAssignment } from '~/utils/providerTeamHelpers';
import { CreateOverrideVisitProvider, OverrideVisitPatient } from './overrideVisitTypes';
import { useSelectableServiceLineIds } from '~/hooks/useDefaultServiceLines';
import { useCurrentOrg } from '~/providers/GraphqlProvider';
import { useOrganizationalUnitSegmentationEnabled } from '~/hooks/permissions';

export interface OverrideVisitConfigFormProps {
  isEditMode: boolean;
  providerData: CommandIndexProvidersQuery;
  selectedProvider?: CreateOverrideVisitProvider;
  visitData: GetVisitDetailsQuery;
  handleClose: () => void;
  calendarDateTime: Date;
  onSuccess: (visitId: number) => void;
  visitId?: number | null;
  prefilledPatient?: OverrideVisitPatient;
}

type FormValues = {
  mainProviderId: number | undefined;
  providerData: SelectProviderData[];
  patient?: { id: number, title: string };
  serviceLineId: number | undefined;
  organizationalUnitId: number | undefined;
  visitTypeId: number | undefined;
  servicesIds: Set<number>;
  duration: number | undefined;
  date: Date;
};

const OverrideVisitConfigForm: React.FC<OverrideVisitConfigFormProps> = ({
  providerData, selectedProvider, visitData, isEditMode, handleClose, calendarDateTime, onSuccess, prefilledPatient
}) => {
  const orgConfig = useOrgConfig();
  const visitId = visitData?.visitSingle?.id;
  const serviceLineIds = useSelectableServiceLineIds()
  const organizationUserId = useCurrentOrg()?.organizationUserId;
  const organizationalUnitSegmentationEnabled = useOrganizationalUnitSegmentationEnabled();

  const confirmedShiftsStartTimes = providerData?.shift?.filter(shift => {
    return `${shift.providerId}` === selectedProvider?.id.toString() && shift.status === ShiftStatusEnum.Confirmed;
  }).map(shift => {
    return new Date(shift.startTime);
  });

  const closestShiftStartTime = closestTo(calendarDateTime, confirmedShiftsStartTimes ?? []);

  const overrideNewVisitDateTime = new Date(closestShiftStartTime ?? calendarDateTime);

  const { timeLabelWithZone, getTimeFormat } = useZonedDateFormat();

  const { control, handleSubmit, watch, setValue, reset } = useForm<FormValues>({
    defaultValues: {
      providerData: [],
      patient: undefined,
      serviceLineId: undefined,
      organizationalUnitId: undefined,
      visitTypeId: undefined,
      servicesIds: new Set<number>(),
      duration: undefined,
      date: overrideNewVisitDateTime,
    },
    mode: 'onChange'
  });

  const [openVisitWarnings, setOpenVisitWarnings] = React.useState<boolean>(false);
  const [visitWarningLog, setVisitWarningLog] = React.useState<GenerateVisitsWarningsMutation["generateVisitWarnings"] | undefined>();
  
  const [generateVisitWarnings, { loading: generatingVisitsWarnings }] = useGenerateVisitsWarningsMutation();
  const { setErrorMessage: displayError } = useSnackbar();

  const [serviceInstructions, setServiceInstructions] = useState<string | undefined>(undefined);
  const [visitNote, setVisitNote] = useState("");

  const selectedPatient = watch('patient');
  const selectedServiceLineId = watch('serviceLineId');

  const formValues = {
    providerData: watch('providerData'),
    patient: watch('patient'),
    serviceLineId: watch('serviceLineId'),
    organizationalUnitId: watch('organizationalUnitId'),
    visitTypeId: watch('visitTypeId'),
    servicesIds: watch('servicesIds'),
    duration: watch('duration'),
    date: watch('date'),
  };

  const currentServiceLine = formValues.serviceLineId ? orgConfig.serviceLines[formValues.serviceLineId] : undefined;
  const currentVisitType = formValues.visitTypeId ? orgConfig.visitTypes[formValues.visitTypeId] : undefined;
  const visitTypeRequirementIds = new Set(currentVisitType?.visitResourceRequirements.map(vrr => vrr.id) ?? []);

  const existingErrandRequirements: VisitResourceRequirement[] = [];
  const errandRequirementLabels: Record<number, string> = {};
  const seenErrandRequirementIds = new Set<number>();
  for (const resource of visitData?.visitSingle?.visitResources ?? []) {
    if (!resource.errandStatus || resource.visitResourceRequirementId == null || seenErrandRequirementIds.has(resource.visitResourceRequirementId)) {
      continue;
    }
    seenErrandRequirementIds.add(resource.visitResourceRequirementId);
    const errandTypeId = resource.resourceRequirement?.errandType?.id;
    const errandType = errandTypeId ? orgConfig.errandTypes[errandTypeId] : undefined;
    const requirement = errandType?.visitResourceRequirements.find(vrr => vrr.id === resource.visitResourceRequirementId);
    if (!requirement) continue;
    existingErrandRequirements.push(requirement);
    errandRequirementLabels[requirement.id] = errandType?.name ?? 'Errand';
  }

  const initializeFormValues = useCallback(async (data: GetVisitDetailsQuery) => {
    if (data?.visitSingle) {
      const visit = data.visitSingle;

      setValue('visitTypeId', visit.visitTypeId);

      const patientOption = { id: visit.patient?.id as number, title: `${visit.patient?.firstName} ${visit.patient?.lastName}` };
      setValue('patient', patientOption);
      setValue('serviceLineId', visit.episode?.serviceLineId as number);
      setValue('servicesIds', new Set<number>(visit.visitServices.map(vs => vs.service?.id as number)));
      const clinicalResources = visit.visitResources.filter(vr => !vr.errandStatus);
      const durationEnd = clinicalResources.length
        ? new Date(Math.max(...clinicalResources.map(vr => new Date(vr.endTime).getTime())))
        : new Date(visit.endTime);
      setValue('duration', differenceInMinutes(durationEnd, new Date(visit.startTime)));
      setValue('date', new Date(visit.startTime));

      const providerDataArray: SelectProviderData[] = [];
      visit.visitResources.forEach(vr => {
        if (vr.provider?.providerType?.id) {
          const offset = differenceInMinutes(new Date(vr.startTime), new Date(visit.startTime));
          const duration = differenceInMinutes(new Date(vr.endTime), new Date(vr.startTime));

          const providerData = {
            resourceId: vr.provider.id.toString(),
            offset,
            duration,
            _vrrId: vr.visitResourceRequirementId!
          };
          
          providerDataArray.push(providerData);
        }
      });

      setValue('providerData', providerDataArray);
    }
  }, [setValue]);

  // Initialize patient from prefilledPatient when in patient-start mode
  useEffect(() => {
    if (prefilledPatient && !formValues.patient?.id) {
      setValue('patient', { 
        id: prefilledPatient.id, 
        title: `${prefilledPatient.firstName} ${prefilledPatient.lastName}` 
      });
    }
  }, [prefilledPatient, formValues.patient?.id, setValue]);

  useEffect(() => {
    if (!selectedServiceLineId && selectedPatient && Object.keys(orgConfig.serviceLines).length === 1) {
      const serviceLineId = parseInt(Object.keys(orgConfig.serviceLines)[0], 10);
      setValue('serviceLineId', serviceLineId, { shouldValidate: true });
    }
    if (!formValues.patient?.id && !prefilledPatient) {
      initializeFormValues(visitData);
    }
  }, [formValues.patient, formValues.serviceLineId, formValues.visitTypeId, orgConfig.serviceLines, setValue, initializeFormValues, visitData, selectedServiceLineId, selectedPatient, prefilledPatient]);

  
  const {
    data: patientData,
    loading: loadingPatientData,
    error: patientDataError,
  } = useGetPatientInstructionsQuery({
    variables: { patientId: formValues.patient?.id as number },
    skip: !formValues.patient?.id,
    fetchPolicy: 'network-only',
  });

  const timezone = visitData?.visitSingle?.addressTimezone || patientData?.patientSingle?.addressTimezone || undefined;
  const activeEpisode = patientData?.patientSingle?.episodes?.find((episode) =>
    episode.active && episode.serviceLineId === formValues.serviceLineId
  );
  const creatingEpisode = !isEditMode && !!formValues.patient?.id && !!formValues.serviceLineId && !activeEpisode;
  const { data: organizationalUnitsData } = useSchedulerOrganizationalUnitsQuery({
    variables: { organizationUserId: organizationUserId ?? 0 },
    skip: !creatingEpisode || !organizationalUnitSegmentationEnabled,
    fetchPolicy: 'network-only',
  });
  const noActiveOrganizationalUnits = !loadingPatientData && !patientDataError
    && patientData?.organizationalUnit.every((unit) => !unit.active) === true;
  const organizationUnits = patientData?.organizationalUnit.filter((unit) => unit.active) ?? [];
  const activeOrganizationalUnits = organizationalUnitSegmentationEnabled === false || organizationUnits.length === 1
    ? organizationUnits
    : organizationalUnitsData?.organizationalUnitOrgUser.flatMap((membership) =>
      membership.organizationalUnit?.active ? [membership.organizationalUnit] : []
    ) ?? [];
  const activeOrganizationUnitCount = organizationUnits.length;
  const organizationalUnitId = isEditMode
    ? patientData?.patientSingle?.episodes?.find((episode) => episode.id === visitData?.visitSingle?.episode?.id)?.organizationalUnitId
    : activeEpisode
      ? activeEpisode.organizationalUnitId
      : getSchedulingOrganizationalUnitId(
        undefined,
        activeOrganizationalUnits,
        formValues.organizationalUnitId,
        activeOrganizationUnitCount === 1,
      );
  const { data: unitProvidersData, loading: loadingUnitProviders, error: unitProvidersError } = useOverrideVisitUnitProvidersQuery({
    variables: { organizationalUnitId: organizationalUnitId ?? 0 },
    skip: !organizationalUnitSegmentationEnabled || organizationalUnitId == null,
    fetchPolicy: 'network-only',
  });
  const providersList = useMemo(() => {
    const providers = providerData?.commandIndexProviders?.providers ?? [];
    if (organizationalUnitSegmentationEnabled === false || noActiveOrganizationalUnits || (!creatingEpisode && organizationalUnitId == null)) return providers;
    if (organizationalUnitId == null || loadingUnitProviders || unitProvidersError) return [];
    const providerIds = new Set(unitProvidersData?.organizationalUnitOrgUser.flatMap((membership) =>
      membership.orgUser?.providers.map((provider) => provider.id) ?? []
    ));
    return providers.filter((provider) => providerIds.has(provider.id));
  }, [providerData, organizationalUnitSegmentationEnabled, noActiveOrganizationalUnits, creatingEpisode, organizationalUnitId, unitProvidersData, loadingUnitProviders, unitProvidersError]);
  const eligibleProviderData = useMemo(() => organizationalUnitSegmentationEnabled === false ? formValues.providerData
    : formValues.providerData.map((provider) =>
      !provider.resourceId || providersList.some((option) => option.id === Number(provider.resourceId))
        ? provider : { ...provider, resourceId: '' }
    ), [organizationalUnitSegmentationEnabled, formValues.providerData, providersList]);

  useEffect(() => {
    if (!organizationalUnitSegmentationEnabled || loadingUnitProviders || unitProvidersError || !unitProvidersData || organizationalUnitId == null) return;
    if (eligibleProviderData.some((provider, index) => provider !== formValues.providerData[index])) {
      setValue('providerData', eligibleProviderData);
    }
  }, [organizationalUnitSegmentationEnabled, loadingUnitProviders, unitProvidersError, unitProvidersData, organizationalUnitId, formValues.providerData, eligibleProviderData, setValue]);

  const editModeLoading = isEditMode && (!formValues.patient || !formValues.providerData);

  const allowedVisitTypes = currentServiceLine?.visitTypeServiceLines.map(vtsl => vtsl.visitTypeId);

  const filteredVisitTypes = Object.values(orgConfig.visitTypes).filter(vt => vt !== undefined).filter(vt => allowedVisitTypes?.includes(vt.id));

  const calculatedDuration = calculateDuration(
    orgConfig,
    formValues.visitTypeId,
    [...formValues.servicesIds.values()].map(String)
  );

  const endTime = new Date(formValues.date.getTime() + (Number(formValues.duration || calculatedDuration) || 0) * 60000).toISOString();

  const autoAssignProviders = (visitTypeId: number) => {
    let used = false;
    const visitType = orgConfig.visitTypes[visitTypeId];
    const newProviderData: SelectProviderData[] = visitType?.visitResourceRequirements.map(vrr => {
      const data = {
        resourceId: "",
        offset: vrr.offset ?? defaultProviderData.offset,
        duration: vrr.duration ?? defaultProviderData.duration,
        _vrrId: vrr.id
      }

      if (used) {
        return data;
      }

      if (selectedProvider?.id && (organizationalUnitSegmentationEnabled === false || providersList.some((provider) => provider.id === selectedProvider.id))) {
        const providerShifts = getProviderShiftsInSameDay(selectedProvider?.id, providerData?.shift, formValues.date.toISOString(), timezone);

        if (
          providerShifts.length &&
          !providerShifts.some(shift => isRequirementEligibleForShift(vrr, shift))
        ) {
          // no eligible shifts
          return data;
        }

        if (selectedProvider && !used && vrr.providerTypes.find(pt => pt.id === selectedProvider.providerType?.id)) {
          used = true;
          data.resourceId = selectedProvider.id.toString();
        }
      }
      return data;
    }) || [];

    newProviderData.forEach(data => {
      const vrr = orgConfig.visitTypes[visitTypeId]?.visitResourceRequirements.find(vrr => vrr.id === data._vrrId);
      if (!vrr) return;
        const assignment = getAutoAssignment(
          vrr,
          newProviderData,
          providersList,
          providerData?.shift || [],
          formValues.date.toISOString(),
          endTime,
          undefined,
        false,
        orgConfig
      );
      const currentData = newProviderData.find(data => data._vrrId === vrr.id);
      if (assignment && currentData) {
        currentData.resourceId = assignment.providerId.toString();
      }
    });

    setValue('providerData', newProviderData);
  };

  const handleVisitTypeChange = (visitTypeId: number) => {
    const visitType = orgConfig.visitTypes[visitTypeId];
    if (!visitType) return;

    setValue('visitTypeId', visitTypeId);
    const serviceDefaults = getServiceDefaults(orgConfig, visitTypeId);
    if (serviceDefaults) {
      setValue('servicesIds', new Set<number>(serviceDefaults.map(serviceId => parseInt(serviceId, 10))));
    }

    if (!isEditMode) {
      autoAssignProviders(visitTypeId);
    }
  };

  const handleServiceLineChange = (serviceLineId: number) => {
    setValue('serviceLineId', serviceLineId);
    setValue('organizationalUnitId', undefined);
    setValue('visitTypeId', undefined);
    setValue('servicesIds', new Set<number>());
  };

  const providerDataForRequirement = (vrrId: number, data: SelectProviderData[] = formValues.providerData) =>
    data.find(provider => provider._vrrId === vrrId);

  const buildResourceFromRequirement = (vrr: VisitResourceRequirement, useOffsetStart: boolean) => {
    const selected = providerDataForRequirement(vrr.id);
    if (!selected?.resourceId) return null;
    const start = useOffsetStart
      ? new Date(formValues.date.getTime() + (selected.offset || 0) * 60000)
      : formValues.date;
    return {
      startTime: start.toISOString(),
      duration: selected.duration ?? vrr.duration,
      providerId: parseInt(selected.resourceId, 10),
      visitResourceRequirementId: selected._vrrId
    };
  };

  const buildResourcesObject = compact([
    ...(currentVisitType?.visitResourceRequirements.map(vrr => buildResourceFromRequirement(vrr, false)) ?? []),
    ...existingErrandRequirements.map(vrr => buildResourceFromRequirement(vrr, true)),
  ]);

  const handleProviderDataChange = (data: SelectProviderData[]) => {
    setValue('providerData', data);
    const visitTypeRows = data.filter(row => visitTypeRequirementIds.has(row._vrrId));
    if (visitTypeRows.length > 1) {
      const newDuration = max(visitTypeRows.map((d => d.offset + d.duration)));
      const currentDuration = watch('duration');
      if (newDuration !== currentDuration) {
        setValue('duration', newDuration);
      }
    }
  };

  const validateForm = (data: Omit<FormValues, "mainProviderId">): string | null => {
    if (!isValid(data.date)) {
      return 'Please enter a valid date and time';
    }

    if (!data.patient?.id || !data.serviceLineId || !data.visitTypeId) {
      return 'Patient, service line, and visit type must be specified';
    }

    if (patientDataError || !patientData?.patientSingle) {
      return 'Unable to load patient details';
    }

    if (creatingEpisode && organizationalUnitId == null && !noActiveOrganizationalUnits) {
      return 'Organizational unit must be specified';
    }

    const allVisitTypeResourcesSelected = currentVisitType?.visitResourceRequirements.every(vrr =>
      providerDataForRequirement(vrr.id, eligibleProviderData)?.resourceId
    );
    const allErrandResourcesSelected = existingErrandRequirements.every(vrr =>
      providerDataForRequirement(vrr.id, data.providerData)?.resourceId
    );

    if (!allVisitTypeResourcesSelected || !allErrandResourcesSelected) {
      return 'A provider must be specified for each role';
    }

    const hasProviderAtStart = currentVisitType?.visitResourceRequirements.some(vrr => {
      const selected = providerDataForRequirement(vrr.id, data.providerData);
      return Boolean(selected?.resourceId) && selected?.offset === 0;
    });

    if (!hasProviderAtStart) {
      return 'At least one provider must be present at the start of the visit';
    }

    return null;
  };

  const validationError = validateForm(formValues);

  const onSubmit = async (data: FormValues) => {
    if (validationError) {
      return;
    }

    if (!data.patient?.id || !data.serviceLineId || !data.visitTypeId) {
      return;
    }

    try {
      const visitWarningsLogData = await generateVisitWarnings({
        variables: {
          patientId: data.patient?.id,
          serviceLineId: data.serviceLineId,
          visitTypeId: data.visitTypeId,
          resources: buildResourcesObject,
          reschedulingVisitId: visitId || null
        },
      });
      setVisitWarningLog(visitWarningsLogData?.data?.generateVisitWarnings ?? undefined);
      setOpenVisitWarnings(true);
    } catch (error) {
      console.error('Error generating visit warnings:', error);
      displayError('An unexpected error occurred');
    }
  };

  const resetForm = useCallback(async () => {
    reset();
    if (visitData) {
      initializeFormValues(visitData);
    }
  }, [reset, visitData, initializeFormValues]);

  const shouldDisableButton = isEditMode ? (
    validationError !== null
  ) : (
    !formValues.patient?.id ||
    !formValues.serviceLineId ||
    !formValues.visitTypeId ||
    validationError !== null
  );

  const providerSelectKey = `${formValues?.patient?.id}-${formValues.visitTypeId}-${providerData?.shift?.length}-${isEditMode ? visitId : 'create'}`;

  const visitWarningModalResources = buildResourcesObject.map((r) => {
    const providerDataValues = formValues.providerData.find((pd) => pd._vrrId === r.visitResourceRequirementId);
    const isVisitTypeResource = visitTypeRequirementIds.has(r.visitResourceRequirementId);
    const isSingleVisitTypeResource = currentVisitType?.visitResourceRequirements.length === 1;
    const calcDuration = isVisitTypeResource && isSingleVisitTypeResource
      ? formValues.duration
      : providerDataValues?.duration;
    return {
      ...r,
      offset: providerDataValues?.offset ?? 0,
      duration: calcDuration ?? r.duration,
    }
  });

  return (
    <>
      {currentServiceLine && currentVisitType && formValues.date && isValid(formValues.date) && formValues.patient?.id && visitWarningLog && buildResourcesObject && (
        <VisitWarningsModal
          open={openVisitWarnings}
          handleClose={() => {
            setOpenVisitWarnings(false);
            handleClose();
          }}
          onSuccess={(visitId) => {
            setOpenVisitWarnings(false);
            onSuccess(visitId);
            resetForm();
            handleClose();
          }}
          duration={parseInt(`${formValues.duration || calculatedDuration}`, 10)}
          providersList={providersList}
          startTime={formValues.date}
          visitWarningLog={visitWarningLog}
          selectedPatient={{
            ...formValues.patient,
            addressTimezone: timezone || '',
            addressState: patientData?.patientSingle?.addressState,
          }}
          selectedProviders={providersList.filter((p) => formValues.providerData.some((pd) => pd.resourceId === `${p.id}`))}
          currentServiceLine={currentServiceLine}
          selectedVisitType={currentVisitType}
          organizationalUnitId={organizationalUnitId ?? undefined}
          serviceIds={[...formValues.servicesIds.values()]}
          resources={visitWarningModalResources}
          reschedulingVisitId={visitId || undefined}
          serviceInstructions={serviceInstructions || patientData?.patientSingle?.serviceInstructions || ""}
          visitNote={visitNote}
        />
      )}
      {!openVisitWarnings && (
        <>
          {/* Sticky Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: formValues.patient ? '1px solid' : 'none', 
            borderBottomColor: 'divider',
            flexShrink: 0,
            overflowX: 'hidden'
          }}>
            <Typography id="modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
              {isEditMode ? 'Edit' : 'Create'} Override Visit for {isValid(formValues.date) ? format(formValues.date, 'PP') : 'Invalid Date'} at {isValid(formValues.date) ? timeLabelWithZone(formValues.date, true, timezone) : 'Invalid Time'}
            </Typography>
            
            {/* Patient, Service Line, Visit Type */}
            {isEditMode ? (
              <Grid container spacing={2} alignItems="center">
                <Grid size={4}>
                  {formValues.patient ? (
                    <>
                      <Typography variant='caption' color="text.secondary">Patient</Typography>
                      <Typography variant="body2" noWrap>{formValues.patient?.title}</Typography>
                    </>
                  ) : <Skeleton variant="text" width="100%" />}
                </Grid>
                
                <Grid size={4}>
                  <Typography variant='caption' color="text.secondary">Service Line</Typography>
                  <Typography variant="body2" noWrap>
                    {currentServiceLine?.name}
                  </Typography>
                </Grid>
                
                <Grid size={4}>
                  <Typography variant='caption' color="text.secondary">Visit Type</Typography>
                  <Typography variant="body2" noWrap>
                    {currentVisitType?.name}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                {/* Show patient as read-only since it was selected in previous step */}
                <Grid size={12}>
                  <Typography variant='caption' color="text.secondary">Patient</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formValues.patient?.title}</Typography>
                </Grid>

                {formValues.patient && (
                  <>
                    <Grid size={6}>
                      <Typography variant='caption' color="text.secondary"><label htmlFor="serviceLineId">Service Line</label></Typography>
                      <Controller
                        name="serviceLineId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            size='small'
                            fullWidth
                            onChange={(e) => handleServiceLineChange(parseInt(`${e.target.value}`, 10))}
                          >
                            {serviceLineIds.map(id => (
                              <MenuItem key={id} value={id}>
                                {orgConfig.serviceLines[id]?.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </Grid>
                    
                    <Grid size={6}>
                      <Typography variant='caption' color="text.secondary">Visit Type</Typography>
                      <Controller
                        name="visitTypeId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            size='small'
                            fullWidth
                            onChange={e => handleVisitTypeChange(parseInt(`${e.target.value}`, 10))}
                            disabled={organizationalUnitSegmentationEnabled && !noActiveOrganizationalUnits
                              && ((creatingEpisode && organizationalUnitId == null)
                                || (organizationalUnitId != null && (loadingUnitProviders || !!unitProvidersError)))}
                          >
                            {filteredVisitTypes?.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </Grid>

                    {creatingEpisode && activeOrganizationUnitCount > 1 && activeOrganizationalUnits.length > 0 && (
                      <Grid size={6}>
                        <Typography variant='caption' color="text.secondary">Organizational Unit</Typography>
                        <Controller
                          name="organizationalUnitId"
                          control={control}
                          render={({ field }) => (
                            <Select {...field} value={field.value ?? ''} size='small' fullWidth>
                              {activeOrganizationalUnits.map((unit) => (
                                <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            )}
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto',
            overflowX: 'hidden',
            p: 2 
          }}>
            {formValues.patient ? (
              <>
                {currentVisitType?.visitTypeServices?.length ? (
                  <Box sx={{ mb: 3 }}>
                    <SelectServices
                      serviceOptions={currentVisitType.visitTypeServices}
                      selectedServicesIds={formValues.servicesIds}
                      setSelectedServicesIds={(ids) => setValue('servicesIds', ids)}
                    />
                  </Box>
                ) : null}

                {currentVisitType && (
                  <Grid container direction='row' spacing={2} mb={3}>
                    <Grid size={3}>
                      <Typography variant='subtitle2'>Date</Typography>
                      <Controller
                        name="date"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            value={field.value}
                            enableAccessibleFieldDOMStructure={false}
                            onChange={(newValue) => newValue && field.onChange(newValue)}
                            slots={{ textField: TextField }}
                            slotProps={{
                              textField: {
                                size: "small",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={3}>
                      <Typography variant='subtitle2'>Start time ({timezone ? timezoneAbbreviation(timezone) : 'UTC'})</Typography>
                      <Controller
                        name="date"
                        control={control}
                        render={({ field }) => (
                          <TimeField
                            format={getTimeFormat(true)}
                            {...field}
                            value={toZonedTime(formValues.date, timezone || 'UTC')}
                            onChange={(newValue) => {
                              if (!newValue || Number.isNaN(newValue.getTime())) {
                                return setValue('date', formValues.date);
                              }

                              setValue('date', fromZonedTime(newValue, timezone || 'UTC'));
                            }}
                            size="small"
                            timezone={timezone}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={3} data-testid="duration_field">
                      <FilterGridRow fullWidth label="Duration" loading={orgConfig.loading}>
                        {currentVisitType.visitResourceRequirements.length > 1 ? (
                          <Typography sx={{ mt: 1 }}>{formValues.duration || calculatedDuration} min</Typography>
                        ) : (
                          <Controller
                            name="duration"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                size='small'
                                value={field.value === 0 ? field.value : (field.value || calculatedDuration)}
                                sx={{ width: '50%' }}
                                onChange={(e) => {
                                  if (!e.target.value) {
                                    field.onChange(0);
                                    return;
                                  }
                                  field.onChange(parseInt(e.target.value, 10));
                                }}
                              />
                            )}
                          />
                        )}
                      </FilterGridRow>
                    </Grid>
                  </Grid>
                )}

                {currentVisitType?.visitResourceRequirements?.length && formValues.patient ? (
                  <Box sx={{ mb: 3 }}>
                    <ProviderSelect
                      key={providerSelectKey}
                      loading={loadingPatientData || loadingUnitProviders}
                      visitResourceRequirements={currentVisitType.visitResourceRequirements}
                      providersList={providersList}
                      prepopulatedProviderList={eligibleProviderData}
                      shifts={providerData?.shift || []}
                      setProviderData={handleProviderDataChange}
                      startTime={formValues.date.toISOString()}
                      endTime={endTime}
                      timezone={timezone}
                      patient={{
                        id: formValues.patient.id,
                        firstName: formValues.patient.title?.split(' ')[0] || '',
                        lastName: formValues.patient.title?.split(' ').slice(1).join(' ') || '',
                        careTeamProviders: patientData?.patientSingle?.careTeamProviders || [],
                        addressState: patientData?.patientSingle?.addressState ?? null,
                      }}
                      strictShiftFiltering={false}
                    />
                    {existingErrandRequirements.length > 0 ? (
                      <ProviderSelect
                        key={`${providerSelectKey}-errands`}
                        loading={false}
                        visitResourceRequirements={existingErrandRequirements}
                        requirementLabels={errandRequirementLabels}
                        providersList={providerData?.commandIndexProviders?.providers || []}
                        prepopulatedProviderList={formValues.providerData}
                        shifts={providerData?.shift || []}
                        setProviderData={handleProviderDataChange}
                        startTime={formValues.date.toISOString()}
                        endTime={endTime}
                        timezone={timezone}
                        offsetFieldsVisibility="always"
                        showAssignmentWarnings={false}
                        patient={{
                          id: formValues.patient.id,
                          firstName: formValues.patient.title?.split(' ')[0] || '',
                          lastName: formValues.patient.title?.split(' ').slice(1).join(' ') || '',
                          careTeamProviders: patientData?.patientSingle?.careTeamProviders || [],
                          addressState: patientData?.patientSingle?.addressState ?? null,
                        }}
                        strictShiftFiltering={false}
                      />
                    ) : null}
                    {validationError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {validationError}
                      </Alert>
                    )}
                  </Box>
                ) : null}
                <Box sx={{ mb: 3 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>Patient Service Instructions</Typography>
                  <ServiceInstructionsInput value={serviceInstructions || patientData?.patientSingle?.serviceInstructions || ""} onChange={setServiceInstructions} />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>Visit Note</Typography>
                  <VisitNoteInput value={visitNote} onChange={setVisitNote} />
                </Box>
              </>
            ) : null}
          </Box>

          {/* Sticky Footer */}
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid', 
            borderTopColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            flexShrink: 0,
            overflowX: 'hidden'
          }}>
            <Button
              onClick={() => {
                handleClose();
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              size='large'
              onClick={handleSubmit(onSubmit)}
              disabled={shouldDisableButton}
              loading={generatingVisitsWarnings || editModeLoading || loadingPatientData}
            >
              Continue
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default OverrideVisitConfigForm;

gql`
  query OverrideVisitUnitProviders($organizationalUnitId: Int!)
  @permission(requires: createOverrideVisits) {
    organizationalUnitOrgUser(where: { organizationalUnitId: { eq: $organizationalUnitId } }) {
      orgUser {
        providers {
          id
        }
      }
    }
  }
`;
